// Lightweight API client helper centralizing base URL & JSON/blob handling.
// Extend with auth token injection or retry logic as needed.
export interface ApiOptions extends RequestInit {
    /** Skip automatic JSON parsing and return raw Response */
    raw?: boolean
    /** Treat non-2xx as error and throw */
    strict?: boolean
    /** 'json' (default) or 'blob' for file download */
    responseType?: 'json' | 'blob'
}

const base = (import.meta as any).env.VITE_API_BASE_URL || ''

// Optional: scopes for access token. Provide via Vite env (comma separated) or leave blank.
const scopeEnv = (import.meta as any).env.VITE_API_SCOPES || ''
const apiScopes: string[] = scopeEnv
    .split(',')
    .map((s: string) => s.trim())
    .filter((s: string) => !!s)

function joinUrl(path: string): string {
    if (/^https?:/i.test(path)) return path // absolute provided
    const root = base.replace(/\/$/, '')
    const suffix = path.replace(/^\//, '')
    return `${root}/${suffix}`
}

export async function getAccessToken(): Promise<string | null> {
    if (!apiScopes.length) return null
    try {
        const { msalInstance } = await import('../auth/msalInstance')
        const account = msalInstance.getActiveAccount()
        if (!account) return null
        const response = await msalInstance.acquireTokenSilent({ scopes: apiScopes, account })
        return response.accessToken || null
    } catch (err) {
        console.warn('[apiClient] acquireTokenSilent failed', err)
        return null
    }
}

export async function api<T = unknown>(path: string, options: ApiOptions & { signal?: AbortSignal } = {}): Promise<T> {
    const { raw, strict = true, headers, responseType = 'json', signal, ...rest } = options;

    const authToken = await getAccessToken();
    const finalHeaders: HeadersInit = {
        Accept: responseType === 'blob' ? '*/*' : 'application/json',
        'Content-Type': responseType === 'blob' ? 'application/octet-stream' : 'application/json',
        ...(options.headers || {}),
    };
    if (authToken) {
        const hasAuth = Object.keys(finalHeaders).some(k => k.toLowerCase() === 'authorization');
        if (!hasAuth) (finalHeaders as any).Authorization = `Bearer ${authToken}`;
    }

    const res = await fetch(joinUrl(path), { headers: finalHeaders, signal, ...rest });

    if (raw) return res as unknown as T;

    if (!res.ok && strict) {
        const err: any = new Error(`API ${res.status} ${res.statusText}`);
        err.status = res.status;
        err.body = await res.text();
        throw err;
    }

    if (responseType === 'blob') {
        const blob = await res.blob();
        return (blob.size ? blob : new Blob()) as unknown as T;
    }

    const text = await res.text();
    try {
        const parsed = text ? JSON.parse(text) : undefined;
        if (Array.isArray(parsed)) return parsed as T;
        if (parsed === null || parsed === undefined) return {} as T;
        return parsed as T;
    } catch {
        return text as unknown as T;
    }
}


export function buildQuery(params: Record<string, any>): string {
    const usp = new URLSearchParams()
    Object.entries(params).forEach(([k, v]) => {
        if (v === undefined || v === null) return
        if (Array.isArray(v)) v.forEach(x => usp.append(k, String(x)))
        else usp.set(k, String(v))
    })
    const s = usp.toString()
    return s ? `?${s}` : ''
}
